/**
 * Playtest session reducer. The semantics are fixed by
 * openspec/changes/playtest-board/notes/engine-contract.md; change that first.
 *
 * Pure: `applyAction` never mutates its input. A rejected action returns the
 * same state object it was given.
 */
import {
  COMMANDER_TAX_PER_CAST,
  DEFAULT_RESHUFFLE_FROM,
  DEFAULT_SWEPT_COMMAND_TO,
  OPENING_HAND_SIZE,
} from './constants';
import { createRngState, type RngState } from './rng';
import { shuffle } from './shuffle';

export type Zone = 'library' | 'hand' | 'battlefield' | 'graveyard' | 'exile' | 'command';
export type Stage = 'deciding' | 'playing';
export type SweptCommandDestination = 'command' | 'library';
export type ReshuffleZone = Exclude<Zone, 'library' | 'command'>;
export type HistorySource = 'opening' | 'draw' | 'mulligan';
export type InstanceId = number;
export type CastDestination = Exclude<Zone, 'library' | 'hand' | 'command'>;
export type MoveTarget = Exclude<Zone, 'library'> | 'library-top' | 'library-bottom';

/** One main-zone entry, as the deck loader hands it in. */
export interface SessionEntry {
  readonly scryfallId: string;
  readonly quantity: number;
  readonly is_commander: boolean;
  readonly is_signature_spell: boolean;
}

/** One physical card in the session. Its id is its index in `SessionState.instances`. */
export interface SessionInstance {
  readonly cardId: string;
  readonly commandZoneCard: boolean;
}

export interface HistoryEntry {
  readonly seq: number;
  readonly game: number;
  readonly instanceId: InstanceId;
  readonly source: HistorySource;
}

/** A host attachment: `instanceId` sits on battlefield card `to` (contract decision 20). */
export interface SessionAttachment {
  readonly instanceId: InstanceId;
  readonly to: InstanceId;
}

export interface SessionState {
  readonly rng: RngState;
  readonly instances: readonly SessionInstance[];
  readonly zones: Readonly<Record<Zone, readonly InstanceId[]>>;
  readonly stage: Stage;
  readonly freeMulligans: number;
  readonly mulligans: number;
  readonly pendingBottom: number;
  readonly game: number;
  readonly castCount: ReadonlyMap<InstanceId, number>;
  readonly history: readonly HistoryEntry[];
  readonly reshuffleFrom: readonly ReshuffleZone[];
  readonly sweptCommandCardsTo: SweptCommandDestination;
  readonly tapped: ReadonlySet<InstanceId>;
  readonly attachments: readonly SessionAttachment[];
}

export type SessionAction =
  | { readonly type: 'draw' }
  | { readonly type: 'mulligan' }
  | { readonly type: 'keep'; readonly bottom: readonly InstanceId[] }
  | { readonly type: 'move'; readonly instanceId: InstanceId; readonly to: MoveTarget }
  | {
      readonly type: 'castFromCommand';
      readonly instanceId: InstanceId;
      readonly to?: CastDestination;
    }
  | { readonly type: 'reorderTop'; readonly instanceIds: readonly InstanceId[] }
  | { readonly type: 'reshuffle'; readonly from: readonly Zone[] }
  | { readonly type: 'setReshuffleSources'; readonly zones: readonly Zone[] }
  | { readonly type: 'setSweptCommandDestination'; readonly to: SweptCommandDestination }
  | { readonly type: 'newGame' }
  | { readonly type: 'setTapped'; readonly instanceId: InstanceId; readonly tapped: boolean }
  | { readonly type: 'untapAll' }
  | { readonly type: 'attach'; readonly instanceId: InstanceId; readonly to: InstanceId }
  | { readonly type: 'detach'; readonly instanceId: InstanceId };

export type SessionResult =
  | 'ok'
  | 'not-kept'
  | 'nothing-to-draw'
  | 'not-deciding'
  | 'empty-hand'
  | 'wrong-bottom-count'
  | 'not-in-hand'
  | 'unknown-instance'
  | 'invalid-zone'
  | 'not-command-card'
  | 'same-zone'
  | 'not-in-command'
  | 'invalid-order'
  | 'invalid-destination'
  | 'not-on-battlefield'
  | 'no-change'
  | 'invalid-host'
  | 'has-attachments'
  | 'not-attached';

export interface ApplyResult {
  readonly state: SessionState;
  readonly result: SessionResult;
}

/** Canonical snapshot (contract "Snapshot"). Key order is the serialisation order. */
export interface SessionSnapshot {
  readonly zones: Record<Zone, InstanceId[]>;
  readonly stage: Stage;
  readonly mulligans: number;
  readonly pendingBottom: number;
  readonly freeMulligans: number;
  readonly game: number;
  readonly castCount: Record<string, number>;
  readonly tapped: InstanceId[];
  readonly attachments: SessionAttachment[];
  readonly reshuffleFrom: ReshuffleZone[];
  readonly sweptCommandCardsTo: SweptCommandDestination;
  readonly history: { seq: number; game: number; instanceId: InstanceId; source: HistorySource }[];
  readonly rng: RngState;
}

/** Mutable working copy used inside one action. Discarded on rejection. */
interface Draft {
  rng: RngState;
  readonly instances: readonly SessionInstance[];
  zones: Record<Zone, InstanceId[]>;
  stage: Stage;
  freeMulligans: number;
  mulligans: number;
  pendingBottom: number;
  game: number;
  castCount: Map<InstanceId, number>;
  history: HistoryEntry[];
  reshuffleFrom: ReshuffleZone[];
  sweptCommandCardsTo: SweptCommandDestination;
  tapped: Set<InstanceId>;
  attachments: SessionAttachment[];
}

export const ZONES: readonly Zone[] = ['library', 'hand', 'battlefield', 'graveyard', 'exile', 'command'];
const RESHUFFLE_ZONES: readonly ReshuffleZone[] = ['hand', 'battlefield', 'graveyard', 'exile'];
const CAST_DESTINATIONS: readonly CastDestination[] = ['battlefield', 'graveyard', 'exile'];
const CAST_DEFAULT: CastDestination = 'battlefield';
export const MOVE_TARGETS: readonly MoveTarget[] = [
  'hand',
  'battlefield',
  'graveyard',
  'exile',
  'command',
  'library-top',
  'library-bottom',
];
export const SWEPT_DESTINATIONS: readonly SweptCommandDestination[] = ['command', 'library'];

function isOneOf<T extends string>(list: readonly T[], value: string): value is T {
  return (list as readonly string[]).includes(value);
}

/** The zones as reshuffle sources, or undefined when any is invalid or repeated. */
function reshuffleZonesOf(zones: readonly Zone[]): readonly ReshuffleZone[] | undefined {
  const distinct = new Set(zones).size === zones.length;
  const valid = distinct && zones.every((zone) => isOneOf(RESHUFFLE_ZONES, zone));
  return valid ? (zones as readonly ReshuffleZone[]) : undefined;
}

/** Where an instance goes when a reshuffle or empty-library draw routes it. */
function routeOnReshuffle(d: Draft, id: InstanceId): Zone {
  const swept = d.instances[id]?.commandZoneCard === true;
  return swept && d.sweptCommandCardsTo === 'command' ? 'command' : 'library';
}

/** Contract decision 20: an instance leaving the battlefield loses its tapped flag and attachment links. */
function clearBattlefieldState(d: Draft, id: InstanceId): void {
  d.tapped.delete(id);
  d.attachments = d.attachments.filter((a) => a.instanceId !== id && a.to !== id);
}

function shuffleLibrary(d: Draft): void {
  const result = shuffle(d.zones.library, d.rng);
  d.zones.library = result.items;
  d.rng = result.rng;
}

/** Contract "reshuffleInto": route every instance of `from`, empty those zones, shuffle the library once. */
function reshuffleInto(d: Draft, from: readonly Zone[]): void {
  for (const zone of from) {
    for (const id of d.zones[zone]) {
      if (zone === 'battlefield') clearBattlefieldState(d, id);
      d.zones[routeOnReshuffle(d, id)].push(id);
    }
  }
  for (const zone of from) {
    d.zones[zone] = [];
  }
  shuffleLibrary(d);
}

/** Moves the top of the library to hand. A no-op on an empty library. */
function drawCard(d: Draft, source: HistorySource): void {
  const top = d.zones.library[0];
  if (top === undefined) return;
  d.zones.library = d.zones.library.slice(1);
  d.zones.hand.push(top);
  d.history.push({ seq: d.history.length, game: d.game, instanceId: top, source });
}

function drawOpeningHand(d: Draft, source: HistorySource): void {
  for (let i = 0; i < OPENING_HAND_SIZE; i++) {
    drawCard(d, source);
  }
}

function draftOf(s: SessionState): Draft {
  return {
    rng: s.rng,
    instances: s.instances,
    zones: {
      library: [...s.zones.library],
      hand: [...s.zones.hand],
      battlefield: [...s.zones.battlefield],
      graveyard: [...s.zones.graveyard],
      exile: [...s.zones.exile],
      command: [...s.zones.command],
    },
    stage: s.stage,
    freeMulligans: s.freeMulligans,
    mulligans: s.mulligans,
    pendingBottom: s.pendingBottom,
    game: s.game,
    castCount: new Map(s.castCount),
    history: [...s.history],
    reshuffleFrom: [...s.reshuffleFrom],
    sweptCommandCardsTo: s.sweptCommandCardsTo,
    tapped: new Set(s.tapped),
    attachments: [...s.attachments],
  };
}

function stateOf(d: Draft): SessionState {
  return {
    rng: d.rng,
    instances: d.instances,
    zones: d.zones,
    stage: d.stage,
    freeMulligans: d.freeMulligans,
    mulligans: d.mulligans,
    pendingBottom: d.pendingBottom,
    game: d.game,
    castCount: d.castCount,
    history: d.history,
    reshuffleFrom: d.reshuffleFrom,
    sweptCommandCardsTo: d.sweptCommandCardsTo,
    tapped: d.tapped,
    attachments: d.attachments,
  };
}

function runDraw(d: Draft): SessionResult {
  if (d.stage === 'deciding') return 'not-kept';
  if (d.zones.library.length === 0) {
    const pool = d.reshuffleFrom.flatMap((zone) => d.zones[zone]);
    if (!pool.some((id) => routeOnReshuffle(d, id) === 'library')) return 'nothing-to-draw';
    reshuffleInto(d, d.reshuffleFrom);
  }
  drawCard(d, 'draw');
  return 'ok';
}

function runMulligan(d: Draft): SessionResult {
  if (d.stage === 'playing') return 'not-deciding';
  if (d.zones.hand.length === 0) return 'empty-hand';
  d.zones.library = [...d.zones.library, ...d.zones.hand];
  d.zones.hand = [];
  shuffleLibrary(d);
  drawOpeningHand(d, 'mulligan');
  d.mulligans += 1;
  d.pendingBottom = Math.min(Math.max(0, d.mulligans - d.freeMulligans), d.zones.hand.length);
  return 'ok';
}

function runKeep(d: Draft, bottom: readonly InstanceId[]): SessionResult {
  if (d.stage === 'playing') return 'not-deciding';
  if (bottom.length !== d.pendingBottom) return 'wrong-bottom-count';
  const chosen = new Set(bottom);
  if (chosen.size !== bottom.length || bottom.some((id) => !d.zones.hand.includes(id))) {
    return 'not-in-hand';
  }
  d.zones.hand = d.zones.hand.filter((id) => !chosen.has(id));
  d.zones.library = [...d.zones.library, ...bottom];
  d.pendingBottom = 0;
  d.stage = 'playing';
  return 'ok';
}

/** Whether a library-end move would leave the card where it already is. */
function atLibraryEnd(library: readonly InstanceId[], instanceId: InstanceId, to: string): boolean {
  if (to === 'library-top') return library[0] === instanceId;
  if (to === 'library-bottom') return library[library.length - 1] === instanceId;
  return false;
}

function runMove(d: Draft, instanceId: InstanceId, to: string): SessionResult {
  const from = ZONES.find((zone) => d.zones[zone].includes(instanceId));
  if (from === undefined) return 'unknown-instance';
  if (!isOneOf(MOVE_TARGETS, to)) return 'invalid-zone';
  if (to === 'command' && d.instances[instanceId]?.commandZoneCard !== true) {
    return 'not-command-card';
  }
  if (to === from) return 'same-zone';
  if (atLibraryEnd(d.zones.library, instanceId, to)) return 'no-change';
  if (from === 'battlefield') clearBattlefieldState(d, instanceId);
  d.zones[from] = d.zones[from].filter((id) => id !== instanceId);
  if (to === 'library-top') {
    d.zones.library = [instanceId, ...d.zones.library];
  } else if (to === 'library-bottom') {
    d.zones.library = [...d.zones.library, instanceId];
  } else {
    d.zones[to].push(instanceId);
  }
  return 'ok';
}

function runCast(d: Draft, instanceId: InstanceId, to: string | undefined): SessionResult {
  if (!d.zones.command.includes(instanceId)) {
    const known = ZONES.some((zone) => d.zones[zone].includes(instanceId));
    return known ? 'not-in-command' : 'unknown-instance';
  }
  const destination = to ?? CAST_DEFAULT;
  if (!isOneOf(CAST_DESTINATIONS, destination)) return 'invalid-zone';
  d.zones.command = d.zones.command.filter((id) => id !== instanceId);
  d.zones[destination].push(instanceId);
  d.castCount.set(instanceId, (d.castCount.get(instanceId) ?? 0) + 1);
  return 'ok';
}

function runReorderTop(d: Draft, instanceIds: readonly InstanceId[]): SessionResult {
  const library = d.zones.library;
  if (instanceIds.length < 1 || instanceIds.length > library.length) return 'invalid-order';
  const top = library.slice(0, instanceIds.length);
  const unique = new Set(instanceIds);
  if (unique.size !== instanceIds.length || instanceIds.some((id) => !top.includes(id))) {
    return 'invalid-order';
  }
  const reordered = [...instanceIds, ...library.slice(instanceIds.length)];
  if (reordered.every((id, index) => id === library[index])) return 'no-change';
  d.zones.library = reordered;
  return 'ok';
}

function runReshuffle(d: Draft, from: readonly Zone[]): SessionResult {
  const valid = reshuffleZonesOf(from);
  if (valid === undefined) return 'invalid-zone';
  reshuffleInto(d, valid);
  return 'ok';
}

function runSetReshuffleSources(d: Draft, zones: readonly Zone[]): SessionResult {
  const valid = reshuffleZonesOf(zones);
  if (valid === undefined) return 'invalid-zone';
  const unchanged =
    valid.length === d.reshuffleFrom.length &&
    valid.every((zone, index) => zone === d.reshuffleFrom[index]);
  if (unchanged) return 'no-change';
  d.reshuffleFrom = [...valid];
  return 'ok';
}

function runSetSweptCommandDestination(d: Draft, to: string): SessionResult {
  if (!isOneOf(SWEPT_DESTINATIONS, to)) return 'invalid-destination';
  if (d.sweptCommandCardsTo === to) return 'no-change';
  d.sweptCommandCardsTo = to;
  return 'ok';
}

function runSetTapped(d: Draft, instanceId: InstanceId, tapped: boolean): SessionResult {
  if (!d.zones.battlefield.includes(instanceId)) return 'not-on-battlefield';
  if (d.tapped.has(instanceId) === tapped) return 'no-change';
  if (tapped) {
    d.tapped.add(instanceId);
  } else {
    d.tapped.delete(instanceId);
  }
  return 'ok';
}

function runUntapAll(d: Draft): SessionResult {
  if (d.tapped.size === 0) return 'no-change';
  d.tapped.clear();
  return 'ok';
}

function runAttach(d: Draft, instanceId: InstanceId, to: InstanceId): SessionResult {
  const known = (id: InstanceId): boolean => ZONES.some((zone) => d.zones[zone].includes(id));
  if (!known(instanceId) || !known(to)) return 'unknown-instance';
  const onBattlefield = (id: InstanceId): boolean => d.zones.battlefield.includes(id);
  if (!onBattlefield(instanceId) || !onBattlefield(to)) return 'not-on-battlefield';
  if (to === instanceId || d.attachments.some((a) => a.instanceId === to)) return 'invalid-host';
  if (d.attachments.some((a) => a.to === instanceId)) return 'has-attachments';
  if (d.attachments.some((a) => a.instanceId === instanceId && a.to === to)) return 'no-change';
  const others = d.attachments.filter((a) => a.instanceId !== instanceId);
  d.attachments = [...others, { instanceId, to }];
  return 'ok';
}

function runDetach(d: Draft, instanceId: InstanceId): SessionResult {
  if (!d.attachments.some((a) => a.instanceId === instanceId)) return 'not-attached';
  d.attachments = d.attachments.filter((a) => a.instanceId !== instanceId);
  return 'ok';
}

/** Splits instance ids into the starting command zone and the library, in id order. */
function startingZones(instances: readonly SessionInstance[]): Record<Zone, InstanceId[]> {
  const command: InstanceId[] = [];
  const library: InstanceId[] = [];
  instances.forEach((instance, id) => {
    if (instance.commandZoneCard) {
      command.push(id);
    } else {
      library.push(id);
    }
  });
  return { library, hand: [], battlefield: [], graveyard: [], exile: [], command };
}

function runNewGame(d: Draft): SessionResult {
  d.zones = startingZones(d.instances);
  d.stage = 'deciding';
  d.mulligans = 0;
  d.pendingBottom = 0;
  d.castCount = new Map();
  d.tapped = new Set();
  d.attachments = [];
  d.game += 1;
  shuffleLibrary(d);
  drawOpeningHand(d, 'opening');
  return 'ok';
}

function run(d: Draft, action: SessionAction): SessionResult {
  switch (action.type) {
    case 'draw':
      return runDraw(d);
    case 'mulligan':
      return runMulligan(d);
    case 'keep':
      return runKeep(d, action.bottom);
    case 'move':
      return runMove(d, action.instanceId, action.to);
    case 'castFromCommand':
      return runCast(d, action.instanceId, action.to);
    case 'reorderTop':
      return runReorderTop(d, action.instanceIds);
    case 'reshuffle':
      return runReshuffle(d, action.from);
    case 'setReshuffleSources':
      return runSetReshuffleSources(d, action.zones);
    case 'setSweptCommandDestination':
      return runSetSweptCommandDestination(d, action.to);
    case 'newGame':
      return runNewGame(d);
    case 'setTapped':
      return runSetTapped(d, action.instanceId, action.tapped);
    case 'untapAll':
      return runUntapAll(d);
    case 'attach':
      return runAttach(d, action.instanceId, action.to);
    case 'detach':
      return runDetach(d, action.instanceId);
  }
}

/**
 * Builds a session: one instance per copy of each entry, in input order.
 * Command-zone instances start in `command`; the rest form the library, which
 * is shuffled from `seed`. The opening hand is then drawn.
 */
export function createSession(input: {
  readonly entries: readonly SessionEntry[];
  readonly seed: number;
  readonly freeMulligans: number;
}): SessionState {
  const instances: SessionInstance[] = [];
  for (const entry of input.entries) {
    const commandZoneCard = entry.is_commander || entry.is_signature_spell;
    for (let copy = 0; copy < entry.quantity; copy++) {
      instances.push({ cardId: entry.scryfallId, commandZoneCard });
    }
  }
  const d: Draft = {
    rng: createRngState(input.seed),
    instances,
    zones: startingZones(instances),
    stage: 'deciding',
    freeMulligans: input.freeMulligans,
    mulligans: 0,
    pendingBottom: 0,
    game: 1,
    castCount: new Map(),
    history: [],
    reshuffleFrom: [...DEFAULT_RESHUFFLE_FROM],
    sweptCommandCardsTo: DEFAULT_SWEPT_COMMAND_TO,
    tapped: new Set(),
    attachments: [],
  };
  shuffleLibrary(d);
  drawOpeningHand(d, 'opening');
  return stateOf(d);
}

/**
 * Applies one action. On `ok` returns a new state; on any other result returns
 * the input state itself (same reference), with the RNG unchanged.
 */
export function applyAction(state: SessionState, action: SessionAction): ApplyResult {
  const d = draftOf(state);
  const result = run(d, action);
  return result === 'ok' ? { state: stateOf(d), result } : { state, result };
}

/** Cards drawn so far this game: history entries whose game is the current game. */
export function drawnCount(state: SessionState): number {
  return state.history.filter((entry) => entry.game === state.game).length;
}

/** Commander tax for an instance: `COMMANDER_TAX_PER_CAST` per earlier cast. Display only. */
export function commanderTax(state: SessionState, instanceId: InstanceId): number {
  return COMMANDER_TAX_PER_CAST * (state.castCount.get(instanceId) ?? 0);
}

/** The contract's canonical snapshot. `JSON.stringify` of it is the vector serialisation. */
export function snapshot(state: SessionState): SessionSnapshot {
  const castCount: Record<string, number> = {};
  const castIds = [...state.castCount.keys()].sort((a, b) => a - b);
  for (const id of castIds) {
    castCount[String(id)] = state.castCount.get(id) ?? 0;
  }
  return {
    zones: {
      library: [...state.zones.library],
      hand: [...state.zones.hand],
      battlefield: [...state.zones.battlefield],
      graveyard: [...state.zones.graveyard],
      exile: [...state.zones.exile],
      command: [...state.zones.command],
    },
    stage: state.stage,
    mulligans: state.mulligans,
    pendingBottom: state.pendingBottom,
    freeMulligans: state.freeMulligans,
    game: state.game,
    castCount,
    tapped: [...state.tapped].sort((a, b) => a - b),
    attachments: state.attachments.map((a) => ({ instanceId: a.instanceId, to: a.to })),
    reshuffleFrom: [...state.reshuffleFrom],
    sweptCommandCardsTo: state.sweptCommandCardsTo,
    history: state.history.map((entry) => ({
      seq: entry.seq,
      game: entry.game,
      instanceId: entry.instanceId,
      source: entry.source,
    })),
    rng: state.rng,
  };
}
